<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseBubble from './Base.vue';
import Icon from 'next/icon/Icon.vue';
import { useMessageContext } from '../provider.js';
import { Button } from 'next/polaris/ui/button';
import { ON_BUBBLE } from 'next/polaris/skin/onBubble';

defineProps({
  icon: { type: [String, Object], required: true },
  senderTranslationKey: { type: String, required: true },
  content: { type: String, required: true },
  title: { type: String, default: '' }, // Title can be any name, description, etc
  action: {
    type: Object,
    required: true,
    validator: action => {
      return action.label && (action.href || action.onClick);
    },
  },
});

const { sender } = useMessageContext();
const { t } = useI18n();

// Inside a Polaris bubble the attachment takes the bubble's own foreground, as
// the kit's MediaBubble does: the old slate text disappears on the carbon
// agent bubble. The icon sits on a tint of that same color.
const look = {
  tile: ON_BUBBLE.surface,
  icon: 'size-4',
  sender: 'type-polaris-body font-medium truncate',
  title: 'type-polaris-body truncate',
  content: 'type-polaris-caption truncate opacity-80',
};

const senderName = computed(() => {
  return sender?.value?.name || '';
});
</script>

<template>
  <BaseBubble class="overflow-hidden p-3" data-bubble-name="attachment">
    <div class="grid gap-4 min-w-64">
      <div class="grid gap-3">
        <div
          class="size-8 rounded-lg grid place-content-center"
          :class="look.tile"
        >
          <slot name="icon">
            <Icon :icon="icon" :class="look.icon" />
          </slot>
        </div>
        <div class="space-y-1 overflow-hidden">
          <div v-if="senderName" :class="look.sender">
            {{
              t(senderTranslationKey, {
                sender: senderName,
              })
            }}
          </div>
          <slot>
            <div v-if="title" :class="look.title">
              {{ title }}
            </div>
            <div v-if="content" :class="look.content">
              {{ content }}
            </div>
          </slot>
        </div>
      </div>
      <div v-if="action" class="mb-1">
        <Button
          v-if="action.href"
          as="a"
          variant="outline"
          :class="ON_BUBBLE.action"
          :href="action.href"
          rel="noreferrer noopener nofollow"
          target="_blank"
        >
          {{ action.label }}
        </Button>
        <Button
          v-else
          variant="outline"
          :class="ON_BUBBLE.action"
          @click="action.onClick"
        >
          {{ action.label }}
        </Button>
      </div>
    </div>
  </BaseBubble>
</template>
